import { useEffect, useMemo, useState } from "react";
import { Download, X } from "lucide-react";
import { safeLocalStorageGet, safeLocalStorageSet } from "@/lib/safe-browser";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
};

const DISMISSED_KEY = "routify:install-prompt-dismissed-at";
const DISMISS_TTL = 3 * 24 * 60 * 60 * 1000;

function isInstalled() {
  if (typeof window === "undefined") return true;
  const standalone = window.matchMedia?.("(display-mode: standalone)").matches;
  const iosStandalone =
    "standalone" in window.navigator &&
    Boolean((window.navigator as Navigator & { standalone?: boolean }).standalone);
  return Boolean(standalone || iosStandalone);
}

function wasDismissedRecently() {
  const raw = safeLocalStorageGet(DISMISSED_KEY);
  if (!raw) return false;
  const value = Number(raw);
  return Number.isFinite(value) && Date.now() - value < DISMISS_TTL;
}

function isLikelyMobileBrowser() {
  if (typeof navigator === "undefined") return false;
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

function isEmbeddedBrowser() {
  if (typeof window === "undefined" || typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  return window.self !== window.top || /;\s*wv\)|\bwv\b|FBAN|FBAV|Instagram|Line\//i.test(ua);
}

/** Instruções manuais corretas por navegador (fallback quando não há evento). */
function manualInstructions(): string {
  if (typeof navigator === "undefined") return "";
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/i.test(ua))
    return "No Safari: botão Partilhar → “Adicionar ao ecrã principal”.";
  if (/Firefox/i.test(ua))
    return "No Firefox: menu ⋮ → “Instalar” ou “Adicionar ao ecrã principal”.";
  if (/SamsungBrowser/i.test(ua))
    return "No Samsung Internet: menu ☰ → “Adicionar página a” → “Ecrã principal”.";
  if (/Android/i.test(ua))
    return "No Chrome: menu ⋮ → “Instalar aplicação” ou “Adicionar ao ecrã principal”.";
  return "No teu navegador: menu do browser → “Instalar aplicação”.";
}

export function InstallAppPrompt() {
  const [promptEvent, setPromptEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [showManualPrompt, setShowManualPrompt] = useState(false);
  const [hidden, setHidden] = useState(true);
  const [cookieBannerVisible, setCookieBannerVisible] = useState(false);

  const canShow = useMemo(() => !hidden && !isInstalled() && !wasDismissedRecently(), [hidden]);

  useEffect(() => {
    if (isInstalled() || wasDismissedRecently()) return;

    const onBeforeInstallPrompt = (event: Event) => {
      // Guardar o evento é obrigatório: só ele permite chamar prompt() depois.
      event.preventDefault();
      setPromptEvent(event as BeforeInstallPromptEvent);
      setShowManualPrompt(false);
      setHidden(false);
    };

    const onInstalled = () => {
      setPromptEvent(null);
      setHidden(true);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onInstalled);

    // O Chrome pode demorar segundos a disparar beforeinstallprompt; só
    // mostramos instruções manuais depois de lhe dar tempo real.
    const fallbackTimer = window.setTimeout(() => {
      if (!isInstalled() && isLikelyMobileBrowser() && !wasDismissedRecently()) {
        if (
          isEmbeddedBrowser() ||
          /iPhone|iPad|iPod|Firefox|SamsungBrowser/i.test(navigator.userAgent)
        ) {
          setShowManualPrompt(true);
          setHidden(false);
        }
      }
    }, 4000);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onInstalled);
      window.clearTimeout(fallbackTimer);
    };
  }, []);

  useEffect(() => {
    const updateCookieOffset = () => {
      setCookieBannerVisible(!safeLocalStorageGet("routify_cookie_consent"));
    };

    updateCookieOffset();
    window.addEventListener("routify-consent-change", updateCookieOffset);
    return () => window.removeEventListener("routify-consent-change", updateCookieOffset);
  }, []);

  if (!canShow) return null;

  const dismiss = () => {
    safeLocalStorageSet(DISMISSED_KEY, String(Date.now()));
    setHidden(true);
  };

  const install = async () => {
    if (!promptEvent) {
      setShowManualPrompt(true);
      return;
    }

    try {
      await promptEvent.prompt();
      const choice = await promptEvent.userChoice.catch(() => null);
      if (choice?.outcome === "accepted") setHidden(true);
      if (choice?.outcome === "dismissed") dismiss();
      setPromptEvent(null);
    } catch {
      // O evento já foi consumido ou o navegador recusou: instruções manuais.
      setPromptEvent(null);
      setShowManualPrompt(true);
    }
  };

  const manualMode = showManualPrompt && !promptEvent;

  return (
    <div
      className="fixed inset-x-3 z-[70] mx-auto max-w-md rounded-2xl border border-border bg-card/95 p-3 text-card-foreground shadow-2xl backdrop-blur-md"
      style={{
        bottom: `calc(env(safe-area-inset-bottom) + ${cookieBannerVisible ? "8.75rem" : "0.75rem"})`,
      }}
    >
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
          <Download className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">Instalar Routify</p>
          <p className="text-xs text-muted-foreground">
            {manualMode
              ? isEmbeddedBrowser()
                ? "Abre no Chrome para instalar."
                : manualInstructions()
              : "Abre mais rápido e fica no ecrã inicial."}
          </p>
        </div>
        {!manualMode && (
          <button
            type="button"
            onClick={install}
            className="rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground transition hover:bg-primary/90"
          >
            Instalar
          </button>
        )}

        <button
          type="button"
          onClick={dismiss}
          aria-label="Fechar sugestão de instalação"
          className="rounded-full p-1.5 text-muted-foreground transition hover:bg-muted hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
